import React, { useState } from "react";
import { useLang } from "@/context/LanguageContext";
import { useConversion } from "@/context/ConversionContext";
import { site } from "@/config/site";
import { Reveal } from "@/components/common/Section";
import { CtaTrio } from "@/components/common/CtaButtons";

function MiniOrienteur() {
  const { t } = useLang();
  const { openForm } = useConversion();
  const m = t.hero.mini;
  const [who, setWho] = useState(null);
  const [situation, setSituation] = useState(null);

  const start = () => {
    openForm({ profile: who?.profile || "", need: situation?.need || "", source: "hero-mini-orienteur" });
  };

  const Choice = ({ active, onClick, children, testid }) => (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      aria-pressed={active}
      className={`rounded-full px-3.5 py-2 text-sm font-medium transition-all ${
        active ? "bg-nek-teal text-white shadow-soft" : "border border-white/20 bg-white/5 text-white/90 hover:border-nek-teal-100 hover:text-white"
      }`}
    >
      {children}
    </button>
  );

  return (
    <div data-testid="hero-mini-orienteur" className="mt-8 max-w-lg rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur-sm">
      <p className="font-heading text-sm font-bold text-white">{m.title}</p>
      <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-nek-teal-100">{m.q1}</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {m.q1Options.map((o) => (
          <Choice key={o.id} active={who?.id === o.id} onClick={() => setWho(o)} testid={`mini-who-${o.id}`}>{o.label}</Choice>
        ))}
      </div>
      <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-nek-teal-100">{m.q2}</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {m.q2Options.map((o) => (
          <Choice key={o.id} active={situation?.id === o.id} onClick={() => setSituation(o)} testid={`mini-situation-${o.id}`}>{o.label}</Choice>
        ))}
      </div>
      <button type="button" onClick={start} disabled={!who || !situation} data-testid="hero-mini-cta" className="btn-coral mt-5 w-full !py-3 disabled:cursor-not-allowed disabled:opacity-50">
        {m.cta}
      </button>
    </div>
  );
}

export default function Hero() {
  const { t } = useLang();
  return (
    <section className="relative overflow-hidden bg-nek-navy" data-testid="hero">
      <div className="absolute inset-0 z-0">
        <img
          src={site.media.heroImage}
          alt="Une famille souriante et complice dans un intérieur lumineux, accompagnée par NEKASSUR"
          className="h-full w-full object-cover object-right"
          loading="eager"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-nek-navy via-nek-navy/80 to-transparent" aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-t from-nek-navy/90 via-nek-navy/20 to-transparent sm:hidden" aria-hidden="true" />
      </div>
      <div className="nek-container relative z-10 flex min-h-[88vh] items-center py-20">
        <div className="max-w-[900px]">
          <Reveal>
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-nek-teal-100 sm:text-sm">
              {t.hero.eyebrow}
            </span>
          </Reveal>
          <Reveal style={{ transitionDelay: "60ms" }}>
            <h1
              className="nek-h1 mt-5 text-white"
              style={{ fontSize: "clamp(3rem, 6vw, 5.125rem)", lineHeight: 1.05, fontWeight: 700, textWrap: "balance" }}
            >
              <span className="block">{t.hero.h1a}</span>
              <span className="block text-nek-teal-100">{t.hero.h1b}</span>
            </h1>
          </Reveal>
          <Reveal style={{ transitionDelay: "120ms" }}>
            <p className="mt-7 max-w-xl text-base leading-relaxed text-white/85 sm:text-lg">{t.hero.intro}</p>
          </Reveal>
          <Reveal style={{ transitionDelay: "160ms" }}>
            <p className="mt-4 font-heading text-base font-semibold text-nek-teal-100 sm:text-lg">{t.hero.conviction}</p>
          </Reveal>
          <Reveal style={{ transitionDelay: "220ms" }}>
            <MiniOrienteur />
          </Reveal>
          <Reveal style={{ transitionDelay: "280ms" }}>
            <div className="mt-6"><CtaTrio variant="dark" /></div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
